import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Lock, PlayCircle, Clock, Check, Award, Star } from "lucide-react";
import { toast } from "sonner";
import { api, API, WILDER_AVATAR } from "@/lib/api";
import { LessonPlayer } from "@/components/LessonPlayer";

const lessonMedia = (li, nLessons, recipe) => {
  const media = recipe.steps_media || [];
  if (!media.length) return [];
  const per = Math.max(1, Math.floor(media.length / nLessons));
  const start = (li - 1) * per;
  if (start >= media.length) return [];
  const end = li === nLessons ? media.length : Math.min(start + per, media.length);
  return media.slice(start, end).map((m, k) => ({ ...m, idx: start + k }));
};

const LessonSteps = ({ items, steps, lesson }) => {
  const [i, setI] = useState(0);
  const [auto, setAuto] = useState(false);
  if (!items.length) return null;
  const cur = items[i];
  const go = (n) => { setI(n); setAuto(true); };
  return (
    <div className="mt-4 border border-[#2A2A2A] bg-[#141414]" data-testid={`lesson-steps-${lesson}`}>
      <img src={cur.img} alt={`Wilder esegue il passo ${cur.idx + 1}`} loading="lazy"
        className="w-full aspect-video object-cover border-b border-[#2A2A2A]" data-testid={`lesson-step-image-${lesson}`} />
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#E4BD44]" data-testid={`lesson-step-label-${lesson}`}>
            Wilder esegue · Passo {cur.idx + 1}
          </p>
          {items.length > 1 && (
            <div className="flex gap-2">
              <button onClick={() => go(Math.max(0, i - 1))} disabled={i === 0}
                className="w-7 h-7 border border-[#2A2A2A] text-xs hover:border-[#E4BD44] disabled:opacity-40"
                data-testid={`lesson-step-prev-${lesson}`}>←</button>
              <button onClick={() => go(Math.min(items.length - 1, i + 1))} disabled={i === items.length - 1}
                className="w-7 h-7 border border-[#2A2A2A] text-xs hover:border-[#E4BD44] disabled:opacity-40"
                data-testid={`lesson-step-next-${lesson}`}>→</button>
            </div>
          )}
        </div>
        <p className="text-xs text-[#C9C4B8] leading-relaxed mb-3">{steps[cur.idx]}</p>
        <audio controls preload="none" src={cur.audio} key={cur.audio} autoPlay={auto}
          className="w-full h-9" data-testid={`lesson-step-audio-${lesson}`} />
      </div>
    </div>
  );
};

export default function Academy() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [course, setCourse] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [certName, setCertName] = useState("");
  const [stepIdx, setStepIdx] = useState(0);
  const [stepAuto, setStepAuto] = useState(false);
  const [review, setReview] = useState({ rating: 0, comment: "", name: "" });
  const [reviewed, setReviewed] = useState(false);

  useEffect(() => {
    if (sessionId) setReviewed(!!localStorage.getItem(`ws-review-${sessionId}`));
  }, [sessionId]);

  const sendReview = async () => {
    if (!review.rating) return toast.error("Scegli da 1 a 5 stelle");
    try {
      await api.post(`/academy/${sessionId}/review`, review);
      localStorage.setItem(`ws-review-${sessionId}`, "1");
      setReviewed(true);
      toast.success("Grazie per la tua recensione!");
    } catch {
      toast.error("Errore nell'invio, riprova.");
    }
  };

  const goStep = (i) => {
    setStepIdx(i);
    setStepAuto(true);
  };

  useEffect(() => {
    if (!sessionId) return;
    try {
      setCompleted(JSON.parse(localStorage.getItem(`ws-academy-${sessionId}`) || "[]"));
    } catch {
      setCompleted([]);
    }
  }, [sessionId]);

  const toggleLesson = (n, force) => {
    setCompleted((prev) => {
      const has = prev.includes(n);
      if (force === true && has) return prev;
      const next = has && force !== true ? prev.filter((x) => x !== n) : [...prev, n];
      localStorage.setItem(`ws-academy-${sessionId}`, JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    if (!sessionId) return setState("locked");
    setState("checking");
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/academy/${sessionId}`);
        setCourse(data);
        return setState("unlocked");
      } catch (e) {
        if (e.response?.status === 404) return setState("locked");
        if (attempts < 8) return setTimeout(poll, 2000);
        setState("pending");
      }
    };
    poll();
  }, [sessionId, retryKey]);

  if (state === "checking") {
    return (
      <div className="max-w-xl mx-auto px-5 py-32 text-center" data-testid="academy-checking">
        <Loader2 size={48} className="mx-auto mb-6 animate-spin text-[#9a9484]" />
        <h1 className="font-display text-4xl font-black mb-3">Sblocco in corso…</h1>
        <p className="text-sm text-[#C9C4B8]">Wilder sta verificando il tuo accesso. Un attimo di pazienza selvaggia.</p>
      </div>
    );
  }

  if (state === "pending") {
    return (
      <div className="max-w-xl mx-auto px-5 py-32 text-center" data-testid="academy-pending">
        <Loader2 size={48} className="mx-auto mb-6 text-[#E4BD44]" />
        <h1 className="font-display text-4xl font-black mb-3">Pagamento in verifica</h1>
        <p className="text-sm text-[#C9C4B8] mb-10">
          Stiamo ancora confermando il tuo accesso: di solito bastano pochi secondi.
          Nessun problema, il tuo accesso è al sicuro.
        </p>
        <button onClick={() => setRetryKey((k) => k + 1)} className="btn-primary" data-testid="academy-retry-button">
          Riprova ora
        </button>
      </div>
    );
  }

  if (state === "locked" || !course) {
    return (
      <div className="max-w-xl mx-auto px-5 py-32 text-center" data-testid="academy-locked">
        <Lock size={48} className="mx-auto mb-6 text-[#9a9484]" />
        <h1 className="font-display text-4xl font-black mb-3">Area riservata agli studenti</h1>
        <p className="text-sm text-[#C9C4B8] mb-10">
          Questo contenuto si sblocca dopo l'acquisto di un corso o prodotto digitale dello Shop.
        </p>
        <Link to="/shop" className="btn-primary" data-testid="academy-goto-shop">Vai allo Shop</Link>
      </div>
    );
  }

  const c = course.content;
  return (
    <div data-testid="academy-page">
      <section className="border-b border-[#2A2A2A] relative overflow-hidden">
        <img src={c.cover} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-10 py-20">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-4" data-testid="academy-unlocked-badge">
            <CheckCircle2 size={16} /> Academy · Accesso sbloccato
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">{c.title}</h1>
          <p className="text-sm text-[#C9C4B8]">{c.subtitle}</p>
        </div>
      </section>

      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 flex flex-col sm:flex-row gap-6 items-start">
          <img src={WILDER_AVATAR} alt="Wilder, il tuo tutor" className="w-24 h-24 rounded-full border-2 border-[#E4BD44] shrink-0" data-testid="academy-wilder-avatar" />
          <div className="border border-[#2A2A2A] bg-[#141414] p-6 relative">
            <p className="text-xs uppercase tracking-[0.2em] text-[#E4BD44] mb-3">Wilder — il tuo tutor</p>
            <p className="text-sm text-[#FCFCFC] leading-relaxed" data-testid="academy-wilder-intro">{c.wilder_intro}</p>
          </div>
        </div>
      </section>

      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12">
          <h2 className="font-display text-lg font-bold mb-6 flex items-center gap-2"><PlayCircle size={20} className="text-[#E4BD44]" /> Il video del corso</h2>
          <LessonPlayer src={c.video_url} poster={c.video_poster} chapters={c.video_chapters || []}
            storageKey={`${course.product_id}-main`} testId="academy-video" />
          <p className="text-[11px] text-[#9a9484] mt-3">{c.video_label}</p>
        </div>
      </section>

      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12">
          <div className="flex items-end justify-between mb-3">
            <h2 className="font-display text-lg font-bold">Le lezioni</h2>
            <p className="text-xs text-[#C9C4B8]" data-testid="academy-progress-count">
              <span className="font-display text-lg font-black text-[#E4BD44]">{completed.length}</span>
              /{c.lessons.length} completate
            </p>
          </div>
          <div className="h-1.5 bg-[#2A2A2A] mb-8" data-testid="academy-progress-bar">
            <div className="h-full bg-[#E4BD44] transition-all duration-500"
              style={{ width: `${(completed.length / c.lessons.length) * 100}%` }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
            {c.lessons.map((l) => (
              <div key={l.n} className={`bg-[#0A0A0A] p-6 transition-opacity ${completed.includes(l.n) ? "opacity-80" : ""}`} data-testid={`academy-lesson-${l.n}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-display text-3xl font-black text-[#E4BD44]">{String(l.n).padStart(2, "0")}</span>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-[11px] text-[#9a9484]"><Clock size={12} /> {l.duration}</span>
                    <button onClick={() => toggleLesson(l.n)} data-testid={`academy-lesson-toggle-${l.n}`}
                      title={completed.includes(l.n) ? "Segna come da fare" : "Segna come completata"}
                      className={`w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${completed.includes(l.n) ? "bg-[#E4BD44] border-[#E4BD44] text-black" : "border-[#9a9484] text-[#9a9484] hover:border-[#E4BD44] hover:text-[#E4BD44]"}`}>
                      <Check size={15} strokeWidth={3} />
                    </button>
                  </div>
                </div>
                <h3 className={`font-display text-lg font-bold mb-3 ${completed.includes(l.n) ? "line-through decoration-[#E4BD44]/60" : ""}`}>{l.title}</h3>
                <div className="flex gap-3 items-start">
                  <img src={WILDER_AVATAR} alt="Wilder" className="w-8 h-8 rounded-full border border-[#E4BD44] shrink-0 mt-0.5" />
                  <p className="text-xs text-[#C9C4B8] italic leading-relaxed">"{l.wilder_says}"</p>
                </div>
                {l.video && (
                  <LessonPlayer src={l.video} poster={l.poster} chapters={l.chapters || []}
                    storageKey={`${course.product_id}-l${l.n}`} testId={`academy-lesson-video-${l.n}`}
                    onEnded={() => toggleLesson(l.n, true)} />
                )}
                <LessonSteps items={lessonMedia(l.n, c.lessons.length, c.recipe)} steps={c.recipe.steps} lesson={l.n} />
              </div>
            ))}
          </div>
          {completed.length === c.lessons.length && (
            <div className="mt-10 border border-[#E4BD44] bg-[#141414] p-8 text-center" data-testid="academy-certificate-box">
              <Award size={44} className="mx-auto mb-4 text-[#E4BD44]" />
              <h3 className="font-display text-2xl font-black mb-2">Corso completato — complimenti, artista!</h3>
              <p className="text-sm text-[#C9C4B8] mb-6 max-w-lg mx-auto">
                Ti sei guadagnato l'attestato ufficiale della Wildersquad Academy, firmato da Wilder e dallo Chef.
                Scrivi il tuo nome come vuoi che appaia sul certificato.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-lg mx-auto">
                <input value={certName} onChange={(e) => setCertName(e.target.value)} maxLength={60}
                  placeholder="Nome e Cognome" data-testid="academy-cert-name"
                  className="bg-[#0A0A0A] border border-[#2A2A2A] px-4 py-3 text-sm flex-1 focus:outline-none focus:border-[#E4BD44]" />
                <a href={certName.trim().length >= 2 ? `${API}/academy/${sessionId}/certificate?name=${encodeURIComponent(certName.trim())}` : undefined}
                  onClick={(e) => { if (certName.trim().length < 2) e.preventDefault(); }}
                  className={`btn-primary inline-flex justify-center ${certName.trim().length < 2 ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="academy-cert-download">
                  <Download size={15} /> Scarica l'attestato
                </a>
              </div>
            </div>
          )}
          {completed.length >= Math.ceil(c.lessons.length / 2) && (
            <div className="mt-6 border border-[#2A2A2A] bg-[#141414] p-8 text-center" data-testid="academy-review-box">
                {reviewed ? (
                  <p className="text-sm text-[#E4BD44]" data-testid="academy-review-thanks">
                    ★ Recensione inviata — grazie, la crew ti ringrazia!
                  </p>
                ) : (
                  <>
                    <h4 className="font-display text-lg font-bold mb-2">Com'è andata? Lascia una recensione</h4>
                    <p className="text-xs text-[#9a9484] mb-4">La tua valutazione aiuta altri artisti a scegliere il corso.</p>
                    <div className="flex justify-center gap-1 mb-4" data-testid="academy-review-stars">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <button key={i} onClick={() => setReview({ ...review, rating: i })} data-testid={`review-star-${i}`}
                          aria-label={`${i} stelle`} className="transition-transform hover:scale-110">
                          <Star size={28} className={i <= review.rating ? "fill-[#E4BD44] text-[#E4BD44]" : "text-[#3a3a3a]"} />
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-col gap-3 max-w-lg mx-auto">
                      <input value={review.name} onChange={(e) => setReview({ ...review, name: e.target.value })} maxLength={60}
                        placeholder="Il tuo nome (facoltativo)" data-testid="academy-review-name"
                        className="bg-[#0A0A0A] border border-[#2A2A2A] px-4 py-2.5 text-sm focus:outline-none focus:border-[#E4BD44]" />
                      <textarea value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} maxLength={500}
                        placeholder="Racconta com'è andata (facoltativo)" rows={3} data-testid="academy-review-comment"
                        className="bg-[#0A0A0A] border border-[#2A2A2A] px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-[#E4BD44]" />
                      <button onClick={sendReview} className="btn-outline self-center" data-testid="academy-review-submit">
                        Invia recensione
                      </button>
                    </div>
                  </>
                )}
              </div>
          )}
        </div>
      </section>

      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12">
          <h2 className="font-display text-lg font-bold mb-2">La ricetta dello Chef</h2>
          <p className="text-xs text-[#9a9484] mb-8">Direttamente dal repertorio Wild & Chic di Antonio Blandolino</p>
          <div className="border border-[#2A2A2A]">
            <div className="p-6 border-b border-[#2A2A2A] bg-[#141414]">
              <h3 className="font-display text-2xl font-bold text-[#E4BD44] mb-2" data-testid="academy-recipe-title">{c.recipe.title}</h3>
              <p className="text-sm text-[#C9C4B8] leading-relaxed mb-3">{c.recipe.story}</p>
              <p className="text-xs text-[#9a9484]">Per {c.recipe.servings} · {c.recipe.time}</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-6 border-b lg:border-b-0 lg:border-r border-[#2A2A2A]">
                <p className="text-xs uppercase tracking-[0.2em] text-[#E4BD44] mb-4">Ingredienti</p>
                <ul className="space-y-2">
                  {c.recipe.ingredients.map((ing) => (
                    <li key={ing} className="text-sm text-[#FCFCFC] flex gap-2"><span className="text-[#E4BD44]">•</span>{ing}</li>
                  ))}
                </ul>
              </div>
              <div className="p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-[#E4BD44] mb-4">Preparazione</p>
                <ol className="space-y-3">
                  {c.recipe.steps.map((s, i) => (
                    <li key={s.slice(0, 30)} className="text-sm text-[#C9C4B8] leading-relaxed flex gap-3">
                      <span className="font-display font-black text-[#E4BD44] shrink-0">{i + 1}.</span>{s}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
          {c.recipe.steps_media?.length > 0 && (
            <div className="mt-12" data-testid="academy-step-player">
              <div className="flex items-center gap-4 mb-2">
                <img src={WILDER_AVATAR} alt="Wilder" className="w-12 h-12 rounded-full border-2 border-[#E4BD44]" />
                <div>
                  <h3 className="font-display text-xl font-black">Wilder esegue — passo per passo</h3>
                  <p className="text-xs text-[#9a9484]">Guarda Wilder all'opera e ascolta la sua voce guidarti, passaggio dopo passaggio</p>
                </div>
              </div>
              <div className="border border-[#2A2A2A] bg-[#141414] mt-4">
                <img src={c.recipe.steps_media[stepIdx].img} alt={`Passo ${stepIdx + 1}`}
                  className="w-full aspect-video object-cover border-b border-[#2A2A2A]" data-testid="step-player-image" />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#E4BD44]" data-testid="step-player-count">
                      Passo {stepIdx + 1} di {c.recipe.steps_media.length}
                    </p>
                    <div className="flex gap-1.5">
                      {c.recipe.steps_media.map((m, i) => (
                        <button key={m.img} onClick={() => goStep(i)} aria-label={`Passo ${i + 1}`}
                          className={`w-2.5 h-2.5 rounded-full transition-colors ${i === stepIdx ? "bg-[#E4BD44]" : "bg-[#2A2A2A] hover:bg-[#9a9484]"}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-[#FCFCFC] leading-relaxed mb-4" data-testid="step-player-text">
                    {c.recipe.steps[stepIdx]}
                  </p>
                  <audio controls preload="none" src={c.recipe.steps_media[stepIdx].audio}
                    key={c.recipe.steps_media[stepIdx].audio} autoPlay={stepAuto}
                    className="w-full mb-5" data-testid="step-player-audio" />
                  <div className="flex justify-between">
                    <button onClick={() => goStep(Math.max(0, stepIdx - 1))} disabled={stepIdx === 0}
                      className="btn-outline !py-2 !px-5 text-xs disabled:opacity-40" data-testid="step-player-prev">
                      ← Passo precedente
                    </button>
                    <button onClick={() => goStep(Math.min(c.recipe.steps_media.length - 1, stepIdx + 1))}
                      disabled={stepIdx === c.recipe.steps_media.length - 1}
                      className="btn-primary !py-2 !px-5 text-xs disabled:opacity-40" data-testid="step-player-next">
                      Passo successivo →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-14 pb-28 text-center">
          <h2 className="font-display text-2xl font-black mb-3">Il tuo Art-Cookbook Wildersquad</h2>
          <p className="text-sm text-[#C9C4B8] mb-8 max-w-xl mx-auto">
            PDF con i piatti iconici dello Chef fotografati come opere d'arte e le ricette complete. Licenza d'uso personale.
          </p>
          <a href={`${API}/academy/${sessionId}/cookbook`} className="btn-primary inline-flex" data-testid="academy-cookbook-download">
            <Download size={15} /> Scarica l'Art-Cookbook (PDF)
          </a>
          <div className="mt-10 flex justify-center gap-3">
            <Link to="/ricette" className="btn-outline" data-testid="academy-goto-ricette">Altre ricette</Link>
            <Link to="/shop" className="btn-outline" data-testid="academy-goto-shop-bottom">Torna allo Shop</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
